import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCustomers, createCustomer, deleteCustomer, getIndustries } from '../services/api';
import Modal from '../components/Modal';
import { useDialog } from '../components/DialogProvider';
import AzotteGrid from '../components/AzotteGrid';
import countries from '../data/countries';
import {
  Box, Typography, Button, TextField, Stack, Grid, Chip, Alert, MenuItem, CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import PeopleIcon from '@mui/icons-material/People';
import ChatIcon from '@mui/icons-material/Chat';
import EventIcon from '@mui/icons-material/Event';
import { DialogActions } from '@mui/material';

export default function CustomerListPage() {
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ companyName: '', industryId: '', email: '', phone: '', address: '', website: '', notes: '', country: '', countryCode: '' });
  const [error, setError] = useState('');
  const [industries, setIndustries] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const navigate = useNavigate();
  const dialog = useDialog();

  const fetchIndustries = useCallback(async () => {
    try { const { data } = await getIndustries(); setIndustries(data); } catch { }
  }, []);

  const fetchCustomers = useCallback(async () => {
    try {
      const { data } = await getCustomers(search || undefined);
      setCustomers(data);
    } catch { setError('Failed to load customers'); }
    finally { setLoading(false); }
  }, [search]);

  useEffect(() => { const t = setTimeout(fetchCustomers, 300); return () => clearTimeout(t); }, [fetchCustomers]);
  useEffect(() => { fetchIndustries(); }, [fetchIndustries]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await createCustomer({ ...form, industryId: form.industryId ? parseInt(form.industryId) : null });
      setShowModal(false);
      setForm({ companyName: '', industryId: '', email: '', phone: '', address: '', website: '', notes: '', country: '', countryCode: '' });
      fetchCustomers();
    } catch { setError('Failed to create customer'); }
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    if (!(await dialog.confirm({ title: 'Delete customer', message: 'Delete this customer?', confirmText: 'Delete', danger: true }))) return;
    try { await deleteCustomer(id); fetchCustomers(); } catch { setError('Failed to delete customer'); }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString() : '-';

  const getScoreColor = (score) => {
    if (score >= 76) return 'success'; if (score >= 51) return 'info';
    if (score >= 26) return 'warning'; return 'error';
  };

  const getProfileField = (customer, key) => {
    if (!customer.profileData) return '';
    try {
      const data = JSON.parse(customer.profileData);
      if (data[key] !== undefined) { const v = data[key]; return (typeof v === 'object' && v !== null && v.value !== undefined) ? v.value : String(v || ''); }
      for (const sv of Object.values(data)) {
        if (typeof sv === 'object' && sv !== null && !sv.value && sv[key] !== undefined) { const v = sv[key]; return (typeof v === 'object' && v !== null && v.value !== undefined) ? v.value : String(v || ''); }
      }
    } catch { }
    return '';
  };

  const columns = [
    { key: 'companyName', header: 'Company', render: (v) => <Typography variant="body2" sx={{ fontWeight: 600 }}>{v}</Typography> },
    { key: 'industryName', header: 'Industry', hideOnMobile: true, render: (v) => v ? <Chip label={v} size="small" color="success" variant="outlined" /> : <Typography color="text.disabled">-</Typography> },
    { key: 'companyType', header: 'Type', hideOnMobile: true, hideOnTablet: true, render: (_, row) => getProfileField(row, 'companyType') || <Typography color="text.disabled">-</Typography> },
    { key: 'country', header: 'Country', hideOnMobile: true, render: (v, row) => v ? <span>{row.countryCode && <strong>{row.countryCode} </strong>}{v}</span> : <Typography color="text.disabled">-</Typography> },
    { key: 'interestLevel', header: 'Interest', hideOnMobile: true, hideOnTablet: true, render: (_, row) => { const il = getProfileField(row, 'interestLevel'); return il ? <Chip label={il} size="small" variant="outlined" /> : <Typography color="text.disabled">-</Typography>; } },
    { key: 'contactCount', header: 'Contacts', render: (v) => <Chip label={v} size="small" /> },
    { key: 'azotteFitScore', header: 'Fit Score', hideOnMobile: true, render: (v) => v > 0 ? <Chip label={v} size="small" color={getScoreColor(v)} /> : <Typography color="text.disabled">-</Typography> },
    { key: 'lastInteractionDate', header: 'Last Interaction', hideOnMobile: true, hideOnTablet: true, render: (v) => formatDate(v) !== '-' ? formatDate(v) : <Typography color="text.disabled">None</Typography> },
    { key: 'actions', header: '', width: 80, render: (_, row) => <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={(e) => handleDelete(e, row.id)}>Delete</Button> },
  ];

  const paginatedRows = customers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5">Customers</Typography>
          <Typography variant="body2" color="text.secondary">Manage your customer relationships</Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setShowModal(true)}>Add Customer</Button>
      </Box>

      {!loading && customers.length > 0 && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { icon: <PeopleIcon />, value: customers.length, label: 'Total Customers', color: 'primary.light' },
            { icon: <ChatIcon />, value: customers.reduce((s, c) => s + (c.contactCount || 0), 0), label: 'Total Contacts', color: 'success.light' },
            { icon: <EventIcon />, value: customers.filter(c => c.lastInteractionDate).length, label: 'With Interactions', color: 'warning.light' },
          ].map((s, i) => (
            <Grid key={i} size={{ xs: 12, sm: 4 }}>
              <Box sx={{ bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ width: 44, height: 44, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: s.color, color: s.color.replace('.light', '.main') }}>{s.icon}</Box>
                <Box>
                  <Typography variant="h6" sx={{ lineHeight: 1.2 }}>{s.value}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>{s.label}</Typography>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      )}

      <TextField placeholder="Search customers by name, industry, or email..." value={search}
        onChange={(e) => setSearch(e.target.value)} sx={{ mb: 2 }} />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress /></Box> :
        customers.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography color="text.secondary" sx={{ mb: 2 }}>No customers found</Typography>
            <Button variant="contained" onClick={() => setShowModal(true)}>Add Your First Customer</Button>
          </Box>
        ) : (
          <AzotteGrid
            columns={columns}
            rows={paginatedRows}
            totalCount={customers.length}
            page={page}
            setPage={setPage}
            rowsPerPage={rowsPerPage}
            handleRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
            loading={loading}
            onDoubleClick={(row) => navigate(`/customers/${row.id}`)}
            actionColumnKey="actions"
          />
        )
      }

      {showModal && (
        <Modal title="Add Customer" onClose={() => setShowModal(false)}>
          <form onSubmit={handleCreate}>
            <Stack spacing={2}>
              <TextField label="Company Name" required value={form.companyName} onChange={(e) => setForm({ ...form, companyName: e.target.value })} />
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField label="Industry" select value={form.industryId} onChange={(e) => setForm({ ...form, industryId: e.target.value })}>
                    <MenuItem value="">-- Select --</MenuItem>
                    {industries.map((ind) => <MenuItem key={ind.id} value={ind.id}>{ind.name}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField label="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField label="Country" select value={form.country}
                    onChange={(e) => { const sel = countries.find(c => c.name === e.target.value); setForm({ ...form, country: e.target.value, countryCode: sel ? sel.code : '' }); }}>
                    <MenuItem value="">-- Select --</MenuItem>
                    {countries.map((c) => <MenuItem key={c.code} value={c.name}>{c.name} ({c.code})</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField label="Country Code" value={form.countryCode} disabled />
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField label="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField label="Website" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} />
                </Grid>
              </Grid>
              <TextField label="Address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
              <TextField label="Notes" multiline rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </Stack>
            <DialogActions sx={{ mt: 2, px: 0 }}>
              <Button onClick={() => setShowModal(false)}>Cancel</Button>
              <Button type="submit" variant="contained">Create Customer</Button>
            </DialogActions>
          </form>
        </Modal>
      )}
    </Box>
  );
}
